// src/components/admin/TargetsEditor.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonLocations } from '../../hooks/useSalonLocations';

const formatWeekLabel = (weekStr) => {
  // weekStr like "2026-W06"
  const match = weekStr.match(/^(\d{4})-W(\d{2})$/);
  if (!match) return weekStr;
  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);
  // ISO week: Jan 4 is always in week 1
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Mon=1..Sun=7
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);
  const monday = new Date(mondayOfWeek1);
  monday.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  const monLabel = monday.toLocaleDateString('en-US', opts);
  const sunLabel = sunday.toLocaleDateString('en-US', opts);
  return `Week of ${monLabel} - ${sunLabel}, ${sunday.getFullYear()}`;
};

const formatMonthLabel = (monthStr) => {
  // monthStr like "2026-02"
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2 });
};

function TargetsEditor({ salonId, onPreview }) {
  const { locations, loading: locLoading } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;

  // '' = All Locations (org-level); string = specific locationId
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const [yearlyTarget, setYearlyTarget] = useState('');
  const [monthlyTarget, setMonthlyTarget] = useState('');
  const [weeklyTarget, setWeeklyTarget] = useState('');
  const [dailyTarget, setDailyTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [lastEdited, setLastEdited] = useState(null);
  const [activeSection, setActiveSection] = useState('defaults');

  // Period-specific targets
  const [monthlyPeriodTargets, setMonthlyPeriodTargets] = useState([]);
  const [weeklyPeriodTargets, setWeeklyPeriodTargets] = useState([]);
  const [dailyPeriodTargets, setDailyPeriodTargets] = useState([]);

  // New period input states
  const [newMonthlyPeriod, setNewMonthlyPeriod] = useState('');
  const [newMonthlyValue, setNewMonthlyValue] = useState('');
  const [newWeeklyPeriod, setNewWeeklyPeriod] = useState('');
  const [newWeeklyValue, setNewWeeklyValue] = useState('');
  const [newDailyPeriod, setNewDailyPeriod] = useState('');
  const [newDailyValue, setNewDailyValue] = useState('');

  useEffect(() => {
    if (!locLoading) loadTargets(selectedLocationId);
  }, [salonId, selectedLocationId, locLoading]);

  const applyTargetData = (t) => {
    setYearlyTarget(t?.yearlyTarget || '');
    setMonthlyTarget(t?.monthlyTarget || '');
    setWeeklyTarget(t?.weeklyTarget || '');
    setDailyTarget(t?.dailyTarget || '');
    setMonthlyPeriodTargets(t?.monthlyPeriodTargets || []);
    setWeeklyPeriodTargets(t?.weeklyPeriodTargets || []);
    setDailyPeriodTargets(t?.dailyPeriodTargets || []);
    setLastEdited(null);
  };

  const loadTargets = async (locationId) => {
    try {
      setLoading(true);
      const salonDoc = await getDoc(doc(db, 'salons', salonId));
      if (salonDoc.exists()) {
        const data = salonDoc.data();
        if (locationId) {
          // Per-location targets stored in locationTargets.{locationId}
          applyTargetData(data.locationTargets?.[locationId] || null);
        } else {
          // Org-level (All Locations)
          applyTargetData(data.targets || null);
        }
      }
    } catch (err) {
      console.error('Error loading targets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleYearlyChange = (value) => {
    const num = parseFloat(value) || 0;
    setYearlyTarget(value);
    setLastEdited('yearly');
    if (value !== '') {
      setMonthlyTarget((num / 12).toFixed(2));
      setWeeklyTarget((num / 52).toFixed(2));
      setDailyTarget((num / 365).toFixed(2));
    } else {
      setMonthlyTarget('');
      setWeeklyTarget('');
      setDailyTarget('');
    }
  };

  const handleMonthlyChange = (value) => {
    const num = parseFloat(value) || 0;
    setMonthlyTarget(value);
    setLastEdited('monthly');
    if (value !== '') {
      setYearlyTarget((num * 12).toFixed(2));
      setWeeklyTarget((num / 4.33).toFixed(2));
      setDailyTarget((num / 30).toFixed(2));
    } else {
      setYearlyTarget('');
      setWeeklyTarget('');
      setDailyTarget('');
    }
  };

  const handleWeeklyChange = (value) => {
    const num = parseFloat(value) || 0;
    setWeeklyTarget(value);
    setLastEdited('weekly');
    if (value !== '') {
      setYearlyTarget((num * 52).toFixed(2));
      setMonthlyTarget((num * 4.33).toFixed(2));
      setDailyTarget((num / 7).toFixed(2));
    } else {
      setYearlyTarget('');
      setMonthlyTarget('');
      setDailyTarget('');
    }
  };

  const handleDailyChange = (value) => {
    const num = parseFloat(value) || 0;
    setDailyTarget(value);
    setLastEdited('daily');
    if (value !== '') {
      setMonthlyTarget((num * 30).toFixed(2));
      setWeeklyTarget((num * 7).toFixed(2));
      setYearlyTarget((num * 365).toFixed(2));
    } else {
      setMonthlyTarget('');
      setWeeklyTarget('');
      setYearlyTarget('');
    }
  };

  // Period target handlers
  const addMonthlyPeriodTarget = () => {
    if (!newMonthlyPeriod || !newMonthlyValue) return;
    const target = parseFloat(newMonthlyValue);
    if (isNaN(target) || target <= 0) return;
    setMonthlyPeriodTargets((prev) => {
      const filtered = prev.filter((p) => p.period !== newMonthlyPeriod);
      const updated = [...filtered, { period: newMonthlyPeriod, target }];
      updated.sort((a, b) => a.period.localeCompare(b.period));
      return updated;
    });
    setNewMonthlyPeriod('');
    setNewMonthlyValue('');
  };

  const removeMonthlyPeriodTarget = (period) => {
    setMonthlyPeriodTargets((prev) => prev.filter((p) => p.period !== period));
  };

  const addWeeklyPeriodTarget = () => {
    if (!newWeeklyPeriod || !newWeeklyValue) return;
    const target = parseFloat(newWeeklyValue);
    if (isNaN(target) || target <= 0) return;
    setWeeklyPeriodTargets((prev) => {
      const filtered = prev.filter((p) => p.period !== newWeeklyPeriod);
      const updated = [...filtered, { period: newWeeklyPeriod, target }];
      updated.sort((a, b) => a.period.localeCompare(b.period));
      return updated;
    });
    setNewWeeklyPeriod('');
    setNewWeeklyValue('');
  };

  const removeWeeklyPeriodTarget = (period) => {
    setWeeklyPeriodTargets((prev) => prev.filter((p) => p.period !== period));
  };

  const addDailyPeriodTarget = () => {
    if (!newDailyPeriod || !newDailyValue) return;
    const target = parseFloat(newDailyValue);
    if (isNaN(target) || target <= 0) return;
    setDailyPeriodTargets((prev) => {
      const filtered = prev.filter((p) => p.period !== newDailyPeriod);
      const updated = [...filtered, { period: newDailyPeriod, target }];
      updated.sort((a, b) => a.period.localeCompare(b.period));
      return updated;
    });
    setNewDailyPeriod('');
    setNewDailyValue('');
  };

  const removeDailyPeriodTarget = (period) => {
    setDailyPeriodTargets((prev) => prev.filter((p) => p.period !== period));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const targetsObj = {
        yearlyTarget: parseFloat(yearlyTarget) || 0,
        monthlyTarget: parseFloat(monthlyTarget) || 0,
        weeklyTarget: parseFloat(weeklyTarget) || 0,
        dailyTarget: parseFloat(dailyTarget) || 0,
        monthlyPeriodTargets,
        weeklyPeriodTargets,
        dailyPeriodTargets,
        updatedAt: new Date(),
      };

      const updatePayload = selectedLocationId
        ? { [`locationTargets.${selectedLocationId}`]: targetsObj }
        : { targets: targetsObj };

      await updateDoc(doc(db, 'salons', salonId), updatePayload);
      setSavedMessage('Targets saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      console.error('Error saving targets:', err);
      setSavedMessage('Error saving targets. Please try again.');
      setTimeout(() => setSavedMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="targets-editor">
        <div className="targets-loading">
          <div className="targets-spinner"></div>
          <p>Loading targets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="targets-editor">
      <div className="editor-page-header">
        <h2>Revenue Targets</h2>
        <div className="editor-page-actions">
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>
      <p className="targets-subtitle">
        Set your revenue targets. Editing one field will auto-calculate the others.
      </p>

      {/* Location selector — multi-location only */}
      {isMultiLocation && (
        <div className="targets-location-selector">
          <span className="targets-location-label">Viewing targets for:</span>
          <div className="targets-location-tabs">
            <button
              className={`targets-loc-tab${selectedLocationId === '' ? ' targets-loc-tab--active' : ''}`}
              onClick={() => setSelectedLocationId('')}
            >
              All Locations
            </button>
            {locations.map(loc => (
              <button
                key={loc.locationId}
                className={`targets-loc-tab${selectedLocationId === loc.locationId ? ' targets-loc-tab--active' : ''}`}
                onClick={() => setSelectedLocationId(loc.locationId)}
              >
                {loc.locationName}
              </button>
            ))}
          </div>
          {selectedLocationId === '' && (
            <p className="targets-location-hint">
              "All Locations" is the org-wide default used when no per-location target is set.
            </p>
          )}
        </div>
      )}

      {/* Section Navigation */}
      <div className="targets-section-nav">
        <button
          className={activeSection === 'defaults' ? 'active' : ''}
          onClick={() => setActiveSection('defaults')}
        >
          Default Targets
        </button>
        <button
          className={activeSection === 'monthly' ? 'active' : ''}
          onClick={() => setActiveSection('monthly')}
        >
          Monthly Periods
        </button>
        <button
          className={activeSection === 'weekly' ? 'active' : ''}
          onClick={() => setActiveSection('weekly')}
        >
          Weekly Periods
        </button>
        <button
          className={activeSection === 'daily' ? 'active' : ''}
          onClick={() => setActiveSection('daily')}
        >
          Daily Periods
        </button>
      </div>

      {/* Default Targets Section */}
      {activeSection === 'defaults' && (
        <>
          <div className="targets-form">
            <div className="target-input-group">
              <label htmlFor="yearly-target">Yearly Target ($)</label>
              <input
                id="yearly-target"
                type="number"
                min="0"
                step="0.01"
                value={yearlyTarget}
                onChange={(e) => handleYearlyChange(e.target.value)}
                placeholder="e.g. 120000"
                className={lastEdited === 'yearly' ? 'primary-input' : ''}
              />
            </div>

            <div className="target-input-group">
              <label htmlFor="monthly-target">
                Monthly Target ($)<span className="default-label">Default</span>
              </label>
              <input
                id="monthly-target"
                type="number"
                min="0"
                step="0.01"
                value={monthlyTarget}
                onChange={(e) => handleMonthlyChange(e.target.value)}
                placeholder="e.g. 10000"
                className={lastEdited === 'monthly' ? 'primary-input' : ''}
              />
            </div>

            <div className="target-input-group">
              <label htmlFor="weekly-target">
                Weekly Target ($)<span className="default-label">Default</span>
              </label>
              <input
                id="weekly-target"
                type="number"
                min="0"
                step="0.01"
                value={weeklyTarget}
                onChange={(e) => handleWeeklyChange(e.target.value)}
                placeholder="e.g. 2500"
                className={lastEdited === 'weekly' ? 'primary-input' : ''}
              />
            </div>

            <div className="target-input-group">
              <label htmlFor="daily-target">
                Daily Target ($)<span className="default-label">Default</span>
              </label>
              <input
                id="daily-target"
                type="number"
                min="0"
                step="0.01"
                value={dailyTarget}
                onChange={(e) => handleDailyChange(e.target.value)}
                placeholder="e.g. 333"
                className={lastEdited === 'daily' ? 'primary-input' : ''}
              />
            </div>
          </div>

          <div className="targets-summary">
            <h3>Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Yearly</span>
                <span className="summary-value">{formatCurrency(yearlyTarget)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Monthly</span>
                <span className="summary-value">{formatCurrency(monthlyTarget)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Weekly</span>
                <span className="summary-value">{formatCurrency(weeklyTarget)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Daily</span>
                <span className="summary-value">{formatCurrency(dailyTarget)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Monthly Periods Section */}
      {activeSection === 'monthly' && (
        <div className="period-section">
          <p className="period-section-description">
            Set specific monthly targets. Months without a specific target will use the
            default of <strong>{formatCurrency(monthlyTarget)}</strong>.
          </p>

          <div className="period-add-form">
            <div className="period-input-group">
              <label>Month</label>
              <input
                type="month"
                value={newMonthlyPeriod}
                onChange={(e) => setNewMonthlyPeriod(e.target.value)}
              />
            </div>
            <div className="period-input-group">
              <label>Target ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newMonthlyValue}
                onChange={(e) => setNewMonthlyValue(e.target.value)}
                placeholder="e.g. 15000"
              />
            </div>
            <button className="period-add-button" onClick={addMonthlyPeriodTarget}>
              Add
            </button>
          </div>

          <div className="period-targets-list">
            {monthlyPeriodTargets.length === 0 ? (
              <p className="period-empty">No monthly period targets set. All months use the default target.</p>
            ) : (
              monthlyPeriodTargets.map((pt) => (
                <div key={pt.period} className="period-target-item">
                  <div className="period-target-info">
                    <span className="period-target-label">{formatMonthLabel(pt.period)}</span>
                    <span className="period-target-value">{formatCurrency(pt.target)}</span>
                  </div>
                  <button
                    className="period-remove-button"
                    onClick={() => removeMonthlyPeriodTarget(pt.period)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Weekly Periods Section */}
      {activeSection === 'weekly' && (
        <div className="period-section">
          <p className="period-section-description">
            Set specific weekly targets. Weeks without a specific target will use the
            default of <strong>{formatCurrency(weeklyTarget)}</strong>.
          </p>

          <div className="period-add-form">
            <div className="period-input-group">
              <label>Week</label>
              <input
                type="week"
                value={newWeeklyPeriod}
                onChange={(e) => setNewWeeklyPeriod(e.target.value)}
              />
            </div>
            <div className="period-input-group">
              <label>Target ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newWeeklyValue}
                onChange={(e) => setNewWeeklyValue(e.target.value)}
                placeholder="e.g. 3500"
              />
            </div>
            <button className="period-add-button" onClick={addWeeklyPeriodTarget}>
              Add
            </button>
          </div>

          <div className="period-targets-list">
            {weeklyPeriodTargets.length === 0 ? (
              <p className="period-empty">No weekly period targets set. All weeks use the default target.</p>
            ) : (
              weeklyPeriodTargets.map((pt) => (
                <div key={pt.period} className="period-target-item">
                  <div className="period-target-info">
                    <span className="period-target-label">{formatWeekLabel(pt.period)}</span>
                    <span className="period-target-value">{formatCurrency(pt.target)}</span>
                  </div>
                  <button
                    className="period-remove-button"
                    onClick={() => removeWeeklyPeriodTarget(pt.period)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Daily Periods Section */}
      {activeSection === 'daily' && (
        <div className="period-section">
          <p className="period-section-description">
            Set specific daily targets. Days without a specific target will use the
            default of <strong>{formatCurrency(dailyTarget)}</strong>.
          </p>

          <div className="period-add-form">
            <div className="period-input-group">
              <label>Date</label>
              <input
                type="date"
                value={newDailyPeriod}
                onChange={(e) => setNewDailyPeriod(e.target.value)}
              />
            </div>
            <div className="period-input-group">
              <label>Target ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newDailyValue}
                onChange={(e) => setNewDailyValue(e.target.value)}
                placeholder="e.g. 800"
              />
            </div>
            <button className="period-add-button" onClick={addDailyPeriodTarget}>
              Add
            </button>
          </div>

          <div className="period-targets-list">
            {dailyPeriodTargets.length === 0 ? (
              <p className="period-empty">No daily period targets set. All days use the default target.</p>
            ) : (
              dailyPeriodTargets.map((pt) => (
                <div key={pt.period} className="period-target-item">
                  <div className="period-target-info">
                    <span className="period-target-label">
                      {new Date(pt.period + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="period-target-value">{formatCurrency(pt.target)}</span>
                  </div>
                  <button
                    className="period-remove-button"
                    onClick={() => removeDailyPeriodTarget(pt.period)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="targets-actions">
        <button
          className="targets-save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Targets'}
        </button>
        {savedMessage && (
          <span className={`targets-save-message ${savedMessage.includes('Error') ? 'error' : 'success'}`}>
            {savedMessage}
          </span>
        )}
      </div>
    </div>
  );
}

export default TargetsEditor;
